// "The door is still open" — what is reminding ME right now, with the snooze
// button beside it. Both shells; drawn at the top of the RF sensors screen,
// which is where a tapped reminder notification lands (`route: '/rf-devices'`,
// api/new/reminders.js).
//
// ⚠️ THE SNOOZE LIVES HERE, NOT ON THE NOTIFICATION. An Android action button
// needs native code and a Play release; this ships by OTA. The push body says
// where to come.
//
// Nothing of its own to show (no reminder running, or the server does not have
// reminders yet) = nothing drawn at all.

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Stack } from '@mui/material';
import SnoozeIcon from '@mui/icons-material/Snooze';
import { useApp } from '../../core/state/AppContext';
import { getMyReminders, snoozeRfReminder } from '../../core/api/api';
import { minutesText, ruleStatus } from '../../core/reminders/reminderRules';
import { parseServerTime } from '../../core/devices/firmwareInfo';
import { showToast } from './toast';

const errText = (e, fallback) => e?.response?.data?.error || e?.message || fallback;
const nameOf = (r) => String(r.Name || '').trim() || 'Sensor';
const placeOf = (r) => String(r.RoomName || r.LocationName || '').trim();

export default function ReminderSnoozeBar({ sx }) {
  const { userId } = useApp();
  const [rules, setRules] = useState([]);
  const [busy, setBusy] = useState(0);

  const load = useCallback(() => {
    if (!userId) return;
    getMyReminders(userId)
      .then((r) => setRules(Array.isArray(r.data?.reminders) ? r.data.reminders : []))
      .catch(() => setRules([]));
  }, [userId]);

  useEffect(() => {
    load();
    // A minute matches the server's sweep — any faster only costs calls.
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const snooze = async (rule, minutes) => {
    setBusy(rule.ReminderId);
    try {
      const r = await snoozeRfReminder(rule.ReminderId, userId, minutes);
      if (r.data && r.data.ok === false) showToast(r.data.error || 'Not snoozed.', { severity: 'error' });
      else showToast(minutes === 0 ? 'Snooze cancelled' : `Snoozed ${minutesText(minutes ?? rule.SnoozeMin)}`, { severity: 'success' });
      load();
    } catch (e) {
      showToast(errText(e, 'Not snoozed.'), { severity: 'error' });
    } finally {
      setBusy(0);
    }
  };

  if (!rules.length) return null;
  return (
    <Box sx={sx}>
      {rules.map((rule) => {
        const s = ruleStatus(rule, { serverTime: parseServerTime });
        const snoozed = s.key === 'snoozed';
        return (
          <Alert
            key={rule.ReminderId}
            severity={snoozed ? 'info' : 'warning'}
            sx={{ mb: 1 }}
            action={(
              <Stack direction="row" spacing={1}>
                {snoozed ? (
                  <Button
                    size="small" color="inherit" startIcon={<SnoozeIcon />}
                    disabled={busy === rule.ReminderId}
                    onClick={() => snooze(rule, 0)}
                  >
                    Remind me again
                  </Button>
                ) : (
                  <Button
                    size="small" color="inherit" variant="outlined" startIcon={<SnoozeIcon />}
                    disabled={busy === rule.ReminderId}
                    onClick={() => snooze(rule)}
                  >
                    {`Snooze ${minutesText(rule.SnoozeMin)}`}
                  </Button>
                )}
              </Stack>
            )}
          >
            <AlertTitle sx={{ mb: 0 }}>
              {`${nameOf(rule)} is still ${String(rule.Meaning || '').toLowerCase()}`}
              {placeOf(rule) ? ` · ${placeOf(rule)}` : ''}
            </AlertTitle>
            {/* ⚠️ ALWAYS WITH ITS AGE — a lost Close frame leaves "Open" standing
                (docs/17), so the app says what it last HEARD and when. */}
            {`Heard ${minutesText(Math.round((Number(rule.StateSec) || 0) / 60))} ago. ${s.text}`}
          </Alert>
        );
      })}
    </Box>
  );
}
